import type { SymbolDisplayProps } from '@src/components/SymbolDisplay/types';
import { UnifiedLiquidityEnum, useSecurity } from '@talos/kyoko';
import { useMemo } from 'react';
import { usePreTradeUnifiedLiquiditySymbolDisplay } from '../../hooks/usePreTradeUnifiedLiquiditySymbolDisplay';
import type { PreTradeResponseData } from '../../redux/preTradeApiTypes';

interface UsePreTradeHeaderSymbolsResult {
  /** True when the header should render a symbol icon. */
  showSymbolIcon: boolean;
  /** Base currency for the symbol icon in single-symbol sessions. */
  symbolIconBaseCurrency?: string;
  /** Deduplicated session symbols rendered as a comma-separated label. */
  symbolList: string;
  /** When set, render SymbolDisplay instead of icon + plain text (unified liquidity). */
  symbolDisplayProps?: Pick<
    SymbolDisplayProps,
    'security' | 'quoteCurrencies' | 'unifiedLiquidity' | 'availableSymbols'
  >;
}

/**
 * Computes display metadata for the Pre-Trade panel header symbol section.
 *
 * Behavior:
 * - Single-symbol session with unified liquidity: SymbolDisplay (e.g. BTC USDT +).
 * - Single-symbol session otherwise: show icon (when base currency is available) and display symbol text.
 * - Multi-symbol session: hide icon and show deduplicated symbols as a comma-separated label.
 */
export function usePreTradeHeaderSymbols(scenarios: PreTradeResponseData[]): UsePreTradeHeaderSymbolsResult {
  const uniqueSymbols = useMemo(() => Array.from(new Set(scenarios.map(scenario => scenario.Symbol))), [scenarios]);
  const isMultiSymbolSession = uniqueSymbols.length > 1;
  const representativeScenario = useMemo(
    () => scenarios.find(scenario => scenario.Symbol === uniqueSymbols[0]) ?? scenarios[0],
    [scenarios, uniqueSymbols]
  );

  const unifiedLiquidityDisplay = usePreTradeUnifiedLiquiditySymbolDisplay(
    isMultiSymbolSession ? undefined : representativeScenario
  );

  const orderSymbol =
    representativeScenario?.NewOrderSingle?.Symbol ?? representativeScenario?.Symbol ?? uniqueSymbols[0];
  const security = useSecurity(isMultiSymbolSession ? undefined : orderSymbol);

  const symbolList = useMemo(() => {
    if (isMultiSymbolSession) {
      return uniqueSymbols.join(', ');
    }
    return security?.DisplaySymbol ?? orderSymbol ?? '';
  }, [isMultiSymbolSession, uniqueSymbols, security?.DisplaySymbol, orderSymbol]);

  const symbolDisplayProps = useMemo(() => {
    if (
      isMultiSymbolSession ||
      !unifiedLiquidityDisplay.isUnifiedLiquidity ||
      unifiedLiquidityDisplay.security == null
    ) {
      return undefined;
    }
    return {
      security: unifiedLiquidityDisplay.security,
      quoteCurrencies: unifiedLiquidityDisplay.quoteCurrencies,
      unifiedLiquidity: UnifiedLiquidityEnum.Enabled,
      availableSymbols: unifiedLiquidityDisplay.availableSymbols,
    };
  }, [
    isMultiSymbolSession,
    unifiedLiquidityDisplay.isUnifiedLiquidity,
    unifiedLiquidityDisplay.security,
    unifiedLiquidityDisplay.quoteCurrencies,
    unifiedLiquidityDisplay.availableSymbols,
  ]);

  return {
    showSymbolIcon: !isMultiSymbolSession && symbolDisplayProps == null,
    symbolIconBaseCurrency: security?.BaseCurrency,
    symbolList,
    symbolDisplayProps,
  };
}
